import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { LegalPanelHeader } from './LegalPanelHeader';
import { LegalPanelInputs } from './LegalPanelInputs';
import { LegalPanelResults } from './LegalPanelResults';

export const LegalPanelSection = ({ caseId, caseData, loadAll }) => {
  const existing = caseData?.legal_panel || null;
  const [panel, setPanel] = useState(existing);
  const [country, setCountry] = useState(existing?.jurisdiction?.country || '');
  const [stateVal, setStateVal] = useState(existing?.jurisdiction?.state || '');
  const [city, setCity] = useState(existing?.jurisdiction?.city || '');
  const [incident, setIncident] = useState(existing?.incident_type || '');
  const [suggesting, setSuggesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [suggestionMeta, setSuggestionMeta] = useState(null);

  useEffect(() => {
    setPanel(caseData?.legal_panel || null);
  }, [caseData?.legal_panel]);

  const suggest = async () => {
    setSuggesting(true);
    setSuggestionMeta(null);
    try {
      const { data } = await api.post(`/cases/${caseId}/legal-panel/suggest`);
      const s = data.suggestion || {};
      if (s.country) setCountry(s.country);
      if (s.state) setStateVal(s.state);
      if (s.city) setCity(s.city);
      if (s.incident_type) setIncident(s.incident_type);
      setSuggestionMeta({ confidence: s.confidence, reasoning: s.reasoning });
    } catch (err) {
      alert(err.response?.data?.detail || 'AI suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    if (!country && !stateVal && !city) {
      alert('Please enter at least a country, state, or city.');
      return;
    }
    if (!incident.trim()) {
      alert('Please enter the incident / issue type.');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/legal-panel/generate`, {
        country, state: stateVal, city, incident_type: incident,
      });
      setPanel(data.legal_panel);
      if (loadAll) loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Panel generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const clearPanel = async () => {
    if (!window.confirm('Clear the saved Legal Panel for this case?')) return;
    setClearing(true);
    try {
      await api.delete(`/cases/${caseId}/legal-panel`);
      setPanel(null);
      setSuggestionMeta(null);
      if (loadAll) loadAll();
    } catch (err) {
      alert('Failed to clear panel');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="legal-panel-section">
      <LegalPanelHeader panel={panel} clearing={clearing} clearPanel={clearPanel} />
      <LegalPanelInputs
        country={country} setCountry={setCountry}
        stateVal={stateVal} setStateVal={setStateVal}
        city={city} setCity={setCity}
        incident={incident} setIncident={setIncident}
        suggest={suggest} suggesting={suggesting}
        generate={generate} generating={generating}
        panel={panel} suggestionMeta={suggestionMeta}
      />
      <LegalPanelResults panel={panel} generating={generating} />
    </div>
  );
};
